import { useState, type FormEvent } from 'react';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { hours, useAttendanceRecords, useAttendanceReport, useRoster, useRosterMutation, type AttendanceRecord, type RosterEmployee, type RosterShift } from '../api/attendance';
import { addDays, formatDate } from '../format';
import { Badge, Button, Empty, ErrorNote, Field, Loading, Modal, useReport, useToast } from '../ui/kit';
import { PageHead } from './shared';

/** The Monday on or before a date. */
const mondayOf = (date: string): string => {
  const wd = new Date(`${date}T00:00:00Z`).getUTCDay(); // 0 = Sunday
  return addDays(date, wd === 0 ? -6 : 1 - wd);
};
const timeOf = (iso: string, tz: string): string => new Intl.DateTimeFormat('en-GB', { timeZone: tz, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(new Date(iso));

type Tab = 'roster' | 'attendance';

export function Roster() {
  const { can } = useAuth();
  const [tab, setTab] = useState<Tab>('roster');
  return (
    <div className="page">
      <PageHead eyebrow="Staff" title="Roster and attendance" />
      {can('attendance.view') ? (
        <div className="seg" role="group" aria-label="Show">
          <button type="button" aria-pressed={tab === 'roster'} onClick={() => setTab('roster')}>Duty roster</button>
          <button type="button" aria-pressed={tab === 'attendance'} onClick={() => setTab('attendance')}>Attendance</button>
        </div>
      ) : null}
      {tab === 'roster' ? <RosterTab /> : <AttendanceTab />}
    </div>
  );
}

/* ------------------------------------------------------------------ roster */
function RosterTab() {
  const { me, can } = useAuth();
  const toast = useToast();
  const report = useReport();
  const [week, setWeek] = useState(mondayOf(me?.property.today ?? ''));
  const to = addDays(week, 6);
  const q = useRoster(week, to);
  const [edit, setEdit] = useState<{ emp: RosterEmployee; date: string; shift?: RosterShift } | null>(null);
  const copy = useRosterMutation((v: { fromDate: string; toDate: string }) => api<{ created: number; skipped: number }>('/roster/copy', { method: 'POST', body: v }));
  const days = Array.from({ length: 7 }, (_, i) => addDays(week, i));
  const manage = can('roster.manage');

  return (
    <>
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <div className="row">
          <Button aria-label="Previous week" onClick={() => setWeek(addDays(week, -7))}>←</Button>
          <strong>{formatDate(week)} – {formatDate(to)}</strong>
          <Button aria-label="Next week" onClick={() => setWeek(addDays(week, 7))}>→</Button>
        </div>
        {manage ? (
          <Button loading={copy.isPending} onClick={() => copy.mutate({ fromDate: week, toDate: addDays(week, 7) }, { onError: report, onSuccess: (r) => toast.ok(`Copied to next week: ${r.created} added, ${r.skipped} left as they were`) })}>Copy to next week</Button>
        ) : null}
      </div>
      {q.isLoading ? (
        <Loading rows={3} />
      ) : q.error ? (
        <ErrorNote error={q.error} retry={() => void q.refetch()} />
      ) : (q.data?.employees ?? []).length === 0 ? (
        <div className="card flat"><Empty icon="people" title="No staff records yet">Add people under People first.</Empty></div>
      ) : (
        <div className="table-scroll">
          <table className="ledger" aria-label="Duty roster">
            <thead>
              <tr>
                <th scope="col">Staff</th>
                {days.map((d) => <th key={d} scope="col">{formatDate(d)}</th>)}
              </tr>
            </thead>
            <tbody>
              {q.data!.employees.map((emp) => (
                <tr key={emp.id}>
                  <th scope="row">{emp.fullName}<div className="small muted">{emp.position}</div></th>
                  {days.map((d) => {
                    const s = q.data!.shifts.find((x) => x.employeeId === emp.id && x.date === d);
                    const leave = q.data!.leave.some((l) => l.employeeId === emp.id && l.from <= d && l.to >= d);
                    const label = s ? `${s.startTime}–${s.endTime}` : leave ? 'Leave' : '—';
                    return (
                      <td key={d}>
                        {manage && !leave ? (
                          <button type="button" className="btn" style={{ minWidth: 92 }} aria-label={`${emp.fullName}, ${formatDate(d)}: ${s ? label : 'no shift'}. ${s ? 'Change' : 'Add a shift'}`} onClick={() => setEdit({ emp, date: d, ...(s ? { shift: s } : {}) })}>{label}</button>
                        ) : (
                          <span className={leave ? 'muted' : ''}>{label}</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {edit ? <ShiftDialog {...edit} onClose={() => setEdit(null)} /> : null}
    </>
  );
}

function ShiftDialog({ emp, date, shift, onClose }: { emp: RosterEmployee; date: string; shift?: RosterShift; onClose: () => void }) {
  const toast = useToast();
  const report = useReport();
  const [start, setStart] = useState(shift?.startTime ?? '07:00');
  const [end, setEnd] = useState(shift?.endTime ?? '15:00');
  const [note, setNote] = useState(shift?.note ?? '');
  const save = useRosterMutation(() => api(`/roster/${emp.id}/${date}`, { method: 'PUT', body: { startTime: start, endTime: end, ...(note.trim() ? { note: note.trim() } : {}) } }));
  const remove = useRosterMutation(() => api(`/roster/${emp.id}/${date}`, { method: 'DELETE' }));
  return (
    <Modal open onClose={onClose} title={`${emp.fullName}: ${formatDate(date)}`}>
      <form className="stack" onSubmit={(e: FormEvent) => { e.preventDefault(); save.mutate(undefined, { onError: report, onSuccess: () => { toast.ok('Shift saved'); onClose(); } }); }}>
        <div className="grid-2">
          <Field id="rs-s" label="Starts"><input id="rs-s" className="input" type="time" value={start} onChange={(e) => setStart(e.target.value)} /></Field>
          <Field id="rs-e" label="Ends" hint="An earlier time than the start means the shift runs past midnight"><input id="rs-e" className="input" type="time" value={end} onChange={(e) => setEnd(e.target.value)} /></Field>
        </div>
        <Field id="rs-n" label="Note (optional)"><input id="rs-n" className="input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. covering the bar" /></Field>
        <div className="modal-foot">
          {shift ? <Button type="button" variant="ghost" loading={remove.isPending} onClick={() => remove.mutate(undefined, { onError: report, onSuccess: () => { toast.ok('Shift removed'); onClose(); } })}>Remove shift</Button> : null}
          <Button type="button" onClick={onClose}>Cancel</Button>
          <Button variant="primary" type="submit" disabled={!start || !end} loading={save.isPending}>Save</Button>
        </div>
      </form>
    </Modal>
  );
}

/* ------------------------------------------------------------- attendance */
function AttendanceTab() {
  const { me, can } = useAuth();
  const [week, setWeek] = useState(mondayOf(me?.property.today ?? ''));
  const to = addDays(week, 6);
  const rep = useAttendanceReport(week, to, true);
  const recs = useAttendanceRecords(week, to, true);
  const tz = me?.property.timezone ?? 'Africa/Accra';
  const [fixing, setFixing] = useState<AttendanceRecord | null>(null);
  const [adding, setAdding] = useState(false);
  const approve = useRosterMutation((v: { id: string; minutes: number }) => api(`/attendance/${v.id}/overtime`, { method: 'POST', body: { minutes: v.minutes } }));
  const toast = useToast();
  const reportErr = useReport();

  return (
    <>
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <div className="row">
          <Button aria-label="Previous week" onClick={() => setWeek(addDays(week, -7))}>←</Button>
          <strong>{formatDate(week)} – {formatDate(to)}</strong>
          <Button aria-label="Next week" onClick={() => setWeek(addDays(week, 7))}>→</Button>
        </div>
        {can('attendance.manage') ? <Button onClick={() => setAdding(true)}>Record attendance</Button> : null}
      </div>

      <div className="eyebrow">The week</div>
      {rep.isLoading ? (
        <Loading rows={2} />
      ) : rep.error ? (
        <ErrorNote error={rep.error} retry={() => void rep.refetch()} />
      ) : (rep.data ?? []).length === 0 ? (
        <div className="card flat"><Empty icon="inbox" title="Nothing rostered or worked this week">Set shifts on the Duty roster tab.</Empty></div>
      ) : (
        <div className="table-scroll">
          <table className="ledger" aria-label="Attendance this week">
            <thead>
              <tr><th scope="col">Staff</th><th scope="col">Rostered</th><th scope="col">Present</th><th scope="col">Late</th><th scope="col">Absent</th><th scope="col">On leave</th><th scope="col">Worked</th><th scope="col">Overtime</th></tr>
            </thead>
            <tbody>
              {rep.data!.map((r) => (
                <tr key={r.employeeId}>
                  <th scope="row">{r.name}</th>
                  <td className="num">{r.rostered}</td>
                  <td className="num">{r.present}</td>
                  <td className="num">{r.late > 0 ? `${r.late} (${hours(r.lateMinutes)})` : 0}</td>
                  <td className="num">{r.absent > 0 ? <Badge tone="bad">{r.absent}</Badge> : 0}</td>
                  <td className="num">{r.onLeave}</td>
                  <td className="num">{hours(r.workedMinutes)}</td>
                  <td className="num">{r.overtimeMinutes > 0 ? `${hours(r.overtimeApprovedMinutes)} of ${hours(r.overtimeMinutes)}` : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="eyebrow">Clock-ins</div>
      {recs.isLoading ? (
        <Loading rows={2} />
      ) : (
        <div className="list">
          {(recs.data ?? []).length === 0 ? <p className="small muted">No one clocked in this week.</p> : null}
          {(recs.data ?? []).map((r) => (
            <div key={r.id} className="item">
              <div className="row" style={{ justifyContent: 'space-between' }}>
                <span className="title">{r.employee}</span>
                <span className="row">
                  {r.stillIn ? <Badge tone={r.stale ? 'bad' : 'info'}>{r.stale ? 'Forgot to clock out?' : 'Still in'}</Badge> : null}
                  {r.lateMinutes > 0 ? <Badge tone="warn">{hours(r.lateMinutes)} late</Badge> : null}
                  {r.earlyLeaveMinutes > 0 ? <Badge tone="warn">left {hours(r.earlyLeaveMinutes)} early</Badge> : null}
                  {r.source === 'MANAGER' ? <Badge>Recorded by a manager</Badge> : null}
                </span>
              </div>
              <div className="small muted">
                {formatDate(r.date)} · {timeOf(r.clockIn, tz)} – {r.clockOut ? timeOf(r.clockOut, tz) : '…'}
                {r.rostered ? ` · rostered ${r.rostered.startTime}–${r.rostered.endTime}` : ' · not rostered'}
                {r.clockOut ? ` · worked ${hours(r.workedMinutes)}` : ''}
              </div>
              {r.note ? <div className="hint">{r.note}</div> : null}
              {r.overtimeMinutes > 0 ? (
                <div className="row small">
                  <span>Overtime {hours(r.overtimeMinutes)}{r.overtimeApprovedMinutes > 0 ? ` · approved ${hours(r.overtimeApprovedMinutes)}` : ' · not approved'}</span>
                  {can('attendance.manage') && r.overtimeApprovedMinutes !== r.overtimeMinutes ? <Button onClick={() => approve.mutate({ id: r.id, minutes: r.overtimeMinutes }, { onError: reportErr, onSuccess: () => toast.ok('Overtime approved') })}>Approve {hours(r.overtimeMinutes)}</Button> : null}
                </div>
              ) : null}
              {can('attendance.manage') ? <div className="row"><Button variant="ghost" onClick={() => setFixing(r)}>Correct</Button></div> : null}
            </div>
          ))}
        </div>
      )}
      {fixing ? <CorrectDialog rec={fixing} tz={tz} onClose={() => setFixing(null)} /> : null}
      {adding ? <RecordDialog week={week} onClose={() => setAdding(false)} /> : null}
    </>
  );
}

/** The date and time a person sees in the hotel's zone, as the inputs of a datetime-local control. */
const localInput = (iso: string | null, tz: string): string => {
  if (!iso) return '';
  const p = Object.fromEntries(new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).formatToParts(new Date(iso)).map((x) => [x.type, x.value]));
  return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`;
};
/** Back to an instant. The hotel's zone is fixed-offset in practice; this works for any zone by checking the offset. */
const toInstant = (local: string, tz: string): string => {
  const asUtc = Date.parse(`${local}:00Z`);
  const shown = Date.parse(`${localInput(new Date(asUtc).toISOString(), tz)}:00Z`);
  return new Date(asUtc - (shown - asUtc)).toISOString();
};

function CorrectDialog({ rec, tz, onClose }: { rec: AttendanceRecord; tz: string; onClose: () => void }) {
  const toast = useToast();
  const report = useReport();
  const [cin, setCin] = useState(localInput(rec.clockIn, tz));
  const [cout, setCout] = useState(localInput(rec.clockOut, tz));
  const [note, setNote] = useState('');
  const save = useRosterMutation(() => api(`/attendance/${rec.id}`, { method: 'PATCH', body: { clockIn: toInstant(cin, tz), clockOut: cout ? toInstant(cout, tz) : null, note: note.trim() } }));
  return (
    <Modal open onClose={onClose} title={`Correct ${rec.employee ?? 'attendance'}`}>
      <form className="stack" onSubmit={(e: FormEvent) => { e.preventDefault(); if (note.trim().length >= 3) save.mutate(undefined, { onError: report, onSuccess: () => { toast.ok('Corrected'); onClose(); } }); }}>
        <Field id="ca-in" label="Clocked in"><input id="ca-in" className="input" type="datetime-local" value={cin} onChange={(e) => setCin(e.target.value)} /></Field>
        <Field id="ca-out" label="Clocked out" hint="Leave empty if the person is still working"><input id="ca-out" className="input" type="datetime-local" value={cout} onChange={(e) => setCout(e.target.value)} /></Field>
        <Field id="ca-n" label="Why is this being corrected?"><input id="ca-n" className="input" value={note} onChange={(e) => setNote(e.target.value)} /></Field>
        <div className="modal-foot">
          <Button type="button" onClick={onClose}>Cancel</Button>
          <Button variant="primary" type="submit" disabled={!cin || note.trim().length < 3} loading={save.isPending}>Save correction</Button>
        </div>
      </form>
    </Modal>
  );
}

function RecordDialog({ week, onClose }: { week: string; onClose: () => void }) {
  const roster = useRoster(week, addDays(week, 6));
  const toast = useToast();
  const report = useReport();
  const [employeeId, setEmployeeId] = useState('');
  const [date, setDate] = useState(week);
  const [cin, setCin] = useState('07:00');
  const [cout, setCout] = useState('15:00');
  const [note, setNote] = useState('');
  const save = useRosterMutation(() => api('/attendance', { method: 'POST', body: { employeeId, date, clockIn: cin, ...(cout ? { clockOut: cout } : {}), note: note.trim() } }));
  return (
    <Modal open onClose={onClose} title="Record attendance">
      <form className="stack" onSubmit={(e: FormEvent) => { e.preventDefault(); if (employeeId && note.trim().length >= 3) save.mutate(undefined, { onError: report, onSuccess: () => { toast.ok('Recorded'); onClose(); } }); }}>
        <Field id="ra-e" label="Who">
          <select id="ra-e" className="select" value={employeeId} onChange={(e) => setEmployeeId(e.target.value)}>
            <option value="">Choose…</option>
            {(roster.data?.employees ?? []).map((e) => <option key={e.id} value={e.id}>{e.fullName}</option>)}
          </select>
        </Field>
        <Field id="ra-d" label="Day"><input id="ra-d" className="input" type="date" value={date} onChange={(e) => setDate(e.target.value)} /></Field>
        <div className="grid-2">
          <Field id="ra-i" label="In"><input id="ra-i" className="input" type="time" value={cin} onChange={(e) => setCin(e.target.value)} /></Field>
          <Field id="ra-o" label="Out"><input id="ra-o" className="input" type="time" value={cout} onChange={(e) => setCout(e.target.value)} /></Field>
        </div>
        <Field id="ra-n" label="Why is this being recorded by hand?"><input id="ra-n" className="input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. phone was flat" /></Field>
        <div className="modal-foot">
          <Button type="button" onClick={onClose}>Cancel</Button>
          <Button variant="primary" type="submit" disabled={!employeeId || !cin || note.trim().length < 3} loading={save.isPending}>Record</Button>
        </div>
      </form>
    </Modal>
  );
}
